/**
 * Finds which module in the bootstrap chunk's static import graph actually failed to load.
 *
 * Chrome's "Failed to fetch dynamically imported module: <url>" always names the root of the
 * import, even when a nested static dependency is what failed, and the TypeError carries nothing
 * else - no cause, no status, no dependency URL. But the browser does remember: per the HTML spec
 * each document's module map stores the result of every module fetch for the document's lifetime,
 * including failures, and never retries them. A <link rel="modulepreload"> for a URL resolves
 * straight from that map without evaluating anything, firing `load` for a module that fetched
 * fine and `error` for one that failed. So walking the graph with modulepreload names the module
 * the browser gave up on, and its resource timing entry (or lack of one) says how it failed.
 */

const MAX_MODULES = 300;
const MAX_DEPTH = 4;
const MAX_REPORTED_FAILURES = 10;
const MAX_REPORTED_UNRESOLVED = 5;
const PRELOAD_TIMEOUT_MS = 2_000;
const SOURCE_FETCH_TIMEOUT_MS = 2_000;
const TOTAL_BUDGET_MS = 4_000;

export type SpecifierResolver = (specifier: string) => string;

export interface FailedModule {
    path: string;
    // The import specifier as written in the parent, e.g. '@angular/core' or './chunk-ABC.js'
    specifier: string;
    parentPath: string | null;
    depth: number;
    // Resource timing for the module's original fetch, captured before the probe ran:
    //  - null:       the browser never put it on the network (extension/AV blocked it, or it was
    //                never requested because a sibling failed first)
    //  - status 0:   network error, blocked or opaque response
    //  - status 200: the response arrived but was rejected (MIME type, truncated/rewritten body)
    originalTiming: { status: number | null; transferSize: number; decodedBodySize: number; durationMs: number } | null;
    // true if the modulepreload started a new network request, i.e. the URL wasn't in the module
    // map yet and this failure is a fresh one rather than the original failure
    probeFetchedFromNetwork: boolean;
    timedOut: boolean;
}

export interface ModuleProbeResult {
    supported: boolean;
    probed: number;
    loaded: number;
    failedCount: number;
    failed: FailedModule[];
    // Bare specifiers the import map couldn't resolve - a missing or null-blocked import map key
    unresolved: string[];
    // Modules that loaded but whose source couldn't be read from the HTTP cache, so their own
    // imports weren't followed - a failure below one of these would be missed
    unexpanded: number;
    hitLimit: boolean;
    durationMs: number;
    error?: string;
}

interface GraphNode {
    url: string;
    specifier: string;
    parentUrl: string | null;
    depth: number;
}

/**
 * Static import specifiers from an esbuild ESM chunk. esbuild hoists every static import to the
 * top of the chunk, so only the leading run of import statements is read - this avoids matching
 * import-like text inside string literals further down, which would send the probe after a URL
 * that never existed and report it as the culprit. Dynamic import() calls are lazy routes, not
 * part of the bootstrap graph, and are deliberately not matched.
 */
export function parseStaticImports(source: string): string[] {
    const specifiers: string[] = [];
    // Leading whitespace, block/line comments (license banners), then one import statement:
    //   import"./a.js";  import{x as y}from"./b.js";  import*as z from"@angular/core";  import d,{e}from"f";
    const leadingImport = /(?:\s|\/\*[\s\S]*?\*\/|\/\/[^\n]*\n)*import\s*(?:[\w$*{}\s,]*?\bfrom\s*)?(["'])([^"'\n]+)\1\s*;?/y;

    let match: RegExpExecArray | null;
    while ((match = leadingImport.exec(source)) !== null) {
        if (match[2]) {
            specifiers.push(match[2]);
        }
    }
    return [...new Set(specifiers)];
}

/**
 * Resolves a specifier the way the browser would for an import inside `parentUrl`. Relative and
 * absolute URLs resolve against the parent; bare specifiers go through the page's import map via
 * `resolveBare` (import.meta.resolve from the entry module), which throws if the map has no entry.
 */
export function resolveModuleSpecifier(specifier: string, parentUrl: string, resolveBare: SpecifierResolver): string {
    if (/^(\.{0,2}\/|[a-z][a-z0-9+.-]*:)/i.test(specifier)) {
        return new URL(specifier, parentUrl).href;
    }
    return resolveBare(specifier);
}

export async function probeModuleMap(rootUrl: string, resolveBare: SpecifierResolver): Promise<ModuleProbeResult> {
    const startedAt = performance.now();
    const result: ModuleProbeResult = {
        supported: document.createElement('link').relList?.supports?.('modulepreload') ?? false,
        probed: 0,
        loaded: 0,
        failedCount: 0,
        failed: [],
        unresolved: [],
        unexpanded: 0,
        hitLimit: false,
        durationMs: 0,
    };

    if (!result.supported) {
        return result;
    }

    try {
        // Snapshot timings first - the probe's own fetches would otherwise be mistaken for the
        // original ones
        const originalTimings = new Map<string, PerformanceResourceTiming>();
        for (const entry of performance.getEntriesByType('resource') as PerformanceResourceTiming[]) {
            originalTimings.set(entry.name, entry);
        }

        const seen = new Set<string>([rootUrl]);
        let frontier: GraphNode[] = [{ url: rootUrl, specifier: rootUrl, parentUrl: null, depth: 0 }];

        // Breadth-first so the shallowest failure is found first and a budget cut-off loses the
        // least useful (deepest) part of the graph
        while (frontier.length > 0) {
            const next: GraphNode[] = [];

            await Promise.all(
                frontier.map(async (node) => {
                    if (performance.now() - startedAt > TOTAL_BUDGET_MS) {
                        result.hitLimit = true;
                        return;
                    }

                    result.probed++;
                    const outcome = await preload(node.url);

                    if (outcome.ok) {
                        result.loaded++;
                    } else {
                        result.failedCount++;
                        if (result.failed.length < MAX_REPORTED_FAILURES) {
                            result.failed.push(describeFailure(node, outcome, originalTimings));
                        }
                        return;
                    }

                    if (node.depth >= MAX_DEPTH) {
                        return;
                    }

                    const source = await readCachedSource(node.url);
                    if (source === null) {
                        result.unexpanded++;
                        return;
                    }

                    for (const specifier of parseStaticImports(source)) {
                        let url: string;
                        try {
                            url = resolveModuleSpecifier(specifier, node.url, resolveBare);
                        } catch {
                            if (result.unresolved.length < MAX_REPORTED_UNRESOLVED) {
                                result.unresolved.push(specifier);
                            }
                            continue;
                        }
                        // Same-origin only - the app's whole import graph is served from one origin,
                        // and only-if-cached source reads require same-origin anyway
                        if (seen.has(url) || !url.startsWith(window.location.origin)) {
                            continue;
                        }
                        if (seen.size >= MAX_MODULES) {
                            result.hitLimit = true;
                            continue;
                        }
                        seen.add(url);
                        next.push({ url, specifier, parentUrl: node.url, depth: node.depth + 1 });
                    }
                })
            );

            frontier = next;
        }
    } catch (error) {
        result.error = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    }

    result.durationMs = Math.round(performance.now() - startedAt);
    return result;
}

interface PreloadOutcome {
    ok: boolean;
    timedOut: boolean;
    fetchedFromNetwork: boolean;
}

function preload(url: string): Promise<PreloadOutcome> {
    const entriesBefore = performance.getEntriesByName(url).length;

    return new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'modulepreload';
        link.href = url;

        const finish = (ok: boolean, timedOut: boolean) => {
            clearTimeout(timer);
            link.remove();
            resolve({ ok, timedOut, fetchedFromNetwork: performance.getEntriesByName(url).length > entriesBefore });
        };
        const timer = setTimeout(() => finish(false, true), PRELOAD_TIMEOUT_MS);

        link.addEventListener('load', () => finish(true, false), { once: true });
        link.addEventListener('error', () => finish(false, false), { once: true });
        document.head.appendChild(link);
    });
}

async function readCachedSource(url: string): Promise<string | null> {
    try {
        // only-if-cached is only allowed with same-origin mode; the caller only passes same-origin URLs
        const response = await fetch(url, {
            cache: 'only-if-cached',
            mode: 'same-origin',
            signal: AbortSignal.timeout(SOURCE_FETCH_TIMEOUT_MS),
        });
        return response.ok ? await response.text() : null;
    } catch {
        return null;
    }
}

function describeFailure(node: GraphNode, outcome: PreloadOutcome, originalTimings: Map<string, PerformanceResourceTiming>): FailedModule {
    const timing = originalTimings.get(node.url);
    return {
        path: new URL(node.url).pathname,
        specifier: node.specifier === node.url ? new URL(node.url).pathname : node.specifier,
        parentPath: node.parentUrl ? new URL(node.parentUrl).pathname : null,
        depth: node.depth,
        originalTiming: timing
            ? {
                  // responseStatus is Chromium-only (109+); Safari and Firefox leave it undefined
                  status: timing.responseStatus ?? null,
                  transferSize: timing.transferSize,
                  decodedBodySize: timing.decodedBodySize,
                  durationMs: Math.round(timing.duration),
              }
            : null,
        probeFetchedFromNetwork: outcome.fetchedFromNetwork,
        timedOut: outcome.timedOut,
    };
}
